import { getDefaultTimezone } from '../core/utils'
import { logError } from '../errors/utils'
import { parseQueryToRequestFilter } from './parse-query-to-request-filter/parse-query-to-request-filter'
import { flattenSchema } from './parse-query-to-request-filter/utils'
import type { IDataSchema } from '../core'
import type { RequestFilter } from '../types'

export const DEFAULT_EMPTY_FILTER: RequestFilter = {
  group: {
    operator: 'OR',
    conditions: [],
  },
}

/**
 * Parses a query string into a RequestFilter.
 * Handles both JSON-encoded RequestFilter objects and Lucene query strings.
 *
 * @param queryString - A JSON-encoded RequestFilter or a Lucene query string
 * @param dataSchema - Optional data schema for field type resolution during Lucene parsing
 * @param defaultFilter - Optional default filter to return on empty input or parse failure
 * @returns A RequestFilter object
 */
export const parseQueryToFilter = (
  queryString: string | undefined,
  dataSchema?: IDataSchema,
  defaultFilter: RequestFilter = DEFAULT_EMPTY_FILTER,
): RequestFilter => {
  if (!queryString || queryString.trim() === '') return defaultFilter

  try {
    const parsed = JSON.parse(queryString)
    if (parsed.group && Array.isArray(parsed.group.conditions))
      return parsed as RequestFilter
  } catch {
    // Not JSON, fall through to lucene parsing
  }

  try {
    const schema = dataSchema ? flattenSchema(dataSchema) : undefined
    return parseQueryToRequestFilter(
      queryString,
      getDefaultTimezone(),
      undefined,
      schema,
    )
  } catch (error) {
    logError(error as Error, {
      message: 'Failed to parse query string',
      queryString,
    })
    return defaultFilter
  }
}
